import { ReactNode } from 'react';
import { ArrowUpDown, Loader2 } from 'lucide-react';

export interface Column<T> {
	key: string;
	header: ReactNode;
	cell: (row: T) => ReactNode;
	width?: string;
	className?: string;
	sortable?: boolean;
}

interface CrmDataTableProps<T extends { id: string }> {
	columns: Column<T>[];
	rows: T[];
	loading?: boolean;
	onRowClick?: (row: T) => void;
	empty?: ReactNode;
	footer?: ReactNode;
}

/**
 * Spreadsheet-style data table used by all CRM list views. Sticky header,
 * thin borders, hover row highlight, click-anywhere-on-row to open detail.
 * Keeps the existing app's neutral palette — no HubSpot orange CTAs.
 */
export function CrmDataTable<T extends { id: string }>({ columns, rows, loading, onRowClick, empty, footer }: CrmDataTableProps<T>) {
	return (
		<div className="overflow-x-auto">
			<table className="w-full text-sm">
				<thead className="bg-muted/40 border-b">
					<tr>
						{columns.map((c) => (
							<th
								key={c.key}
								className={`px-3 py-2 text-left font-medium text-[11px] uppercase tracking-wide text-muted-foreground ${c.className || ''}`}
								style={c.width ? { width: c.width } : undefined}
							>
								<span className="inline-flex items-center gap-1">
									{c.header}
									{c.sortable && <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />}
								</span>
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{loading ? (
						<tr>
							<td colSpan={columns.length} className="px-3 py-10 text-center text-muted-foreground">
								<div className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
							</td>
						</tr>
					) : rows.length === 0 ? (
						<tr>
							<td colSpan={columns.length} className="px-3 py-12 text-center">{empty || 'No records.'}</td>
						</tr>
					) : rows.map((row) => (
						<tr
							key={row.id}
							onClick={onRowClick ? () => onRowClick(row) : undefined}
							className={`border-b last:border-b-0 ${onRowClick ? 'cursor-pointer hover:bg-muted/40' : ''}`}
						>
							{columns.map((c) => (
								<td key={c.key} className={`px-3 py-2.5 align-middle ${c.className || ''}`}>
									{c.cell(row)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
			{footer && <div className="border-t px-3 py-2 text-xs text-muted-foreground">{footer}</div>}
		</div>
	);
}

/** Standard "Name with avatar" cell renderer used across CRM tables. */
export function NameCell({ avatar, name, secondary }: { avatar?: ReactNode; name: ReactNode; secondary?: ReactNode }) {
	return (
		<div className="flex items-center gap-2.5 min-w-0">
			{avatar && <div className="shrink-0">{avatar}</div>}
			<div className="min-w-0">
				<div className="truncate text-sm font-medium text-primary">{name}</div>
				{secondary && <div className="truncate text-xs text-muted-foreground">{secondary}</div>}
			</div>
		</div>
	);
}
